import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Chip
} from "@material-ui/core";
import {
  Cake as CakeIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Phone as PhoneIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import { usePanelStyles, ModalTitle } from "../FormFields/panel";
import { getTokens } from "../../styles/tokens";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    list: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: 10,
      margin: 0,
      padding: 0,
      listStyle: "none"
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      minWidth: 0,
      padding: "12px 14px",
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      [theme.breakpoints.down("xs")]: { flexWrap: "wrap" }
    },
    avatar: {
      flex: "none",
      width: 40,
      height: 40,
      fontSize: 14,
      fontWeight: 600,
      color: t.brand,
      backgroundColor: t.tint.soft
    },
    itemText: { flex: "1 1 160px", minWidth: 0 },
    personName: {
      fontFamily: t.font,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.4,
      color: t.text,
      overflowWrap: "anywhere"
    },
    personType: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 2,
      fontFamily: t.font,
      fontSize: 12.5,
      color: t.textSecondary,
      "& > .MuiSvgIcon-root": { fontSize: 15 }
    },
    itemAction: {
      flex: "none",
      [theme.breakpoints.down("xs")]: { width: "100%", "& .MuiButton-root": { width: "100%" } }
    },
    noDataMessage: {
      padding: "32px 0",
      textAlign: "center",
      fontFamily: t.font,
      fontSize: 13,
      color: t.textSecondary
    }
  };
});

const BirthdayModal = ({ open, onClose, user }) => {
  const classes = useStyles();
  const p = usePanelStyles();
  const [birthdayData, setBirthdayData] = useState({ users: [], contacts: [], settings: null });
  const [loading, setLoading] = useState(false);
  const [sendingMessages, setSendingMessages] = useState(new Set());

  useEffect(() => {
    if (open) {
      fetchTodayBirthdays();
    }
  }, [open]);

  const fetchTodayBirthdays = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/birthdays/today");
      setBirthdayData(data.data);
    } catch (error) {
      toast.error("Erro ao carregar aniversariantes");
    } finally {
      setLoading(false);
    }
  };

  const handleSendBirthdayMessage = async (contactId, contactName) => {
    setSendingMessages(prev => new Set([...prev, contactId]));
    
    try {
      await api.post("/birthdays/send-message", { contactId });
      toast.success(`Mensagem de parabéns enviada para ${contactName}! 🎉`);
      
      // Atualizar o estado local para marcar como enviada
      setBirthdayData(prev => ({
        ...prev,
        contacts: prev.contacts.map(contact => 
          contact.id === contactId 
            ? { ...contact, messageSent: true }
            : contact
        )
      }));
    } catch (error) {
      // Verificar se é erro de mensagem já enviada
      if (error.response?.status === 409) {
        toast.warning("Mensagem de aniversário já foi enviada hoje para este contato");
        
        // Atualizar o estado local para marcar como enviada
        setBirthdayData(prev => ({
          ...prev,
          contacts: prev.contacts.map(contact => 
            contact.id === contactId 
              ? { ...contact, messageSent: true }
              : contact
          )
        }));
      } else {
        toast.error("Erro ao enviar mensagem de aniversário");
      }
    } finally {
      setSendingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(contactId);
        return newSet;
      });
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatAge = (age) => {
    if (!age) return "N/A";
    return `${age} ano${age !== 1 ? 's' : ''}`;
  };

  const hasBirthdays = birthdayData.users.length > 0 || birthdayData.contacts.length > 0;

  if (!hasBirthdays && !loading) {
    return null; // Não mostrar modal se não há aniversariantes
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      classes={{ paper: p.dialogPaper }}
    >
      <ModalTitle
        title="🎉 Parabéns!"
        subtitle="Hoje é um dia especial!"
        onClose={onClose}
      />

      {/* Content */}
      <DialogContent className={p.modalBody}>
        {loading ? (
          <div className={classes.noDataMessage}>Carregando aniversariantes...</div>
        ) : (
          <ul className={classes.list}>
            {/* Aniversariantes Usuários */}
            {birthdayData.users.map((birthdayUser) => (
              <li key={`user-${birthdayUser.id}`} className={classes.item}>
                <Avatar className={classes.avatar}>
                  {getInitials(birthdayUser.name)}
                </Avatar>

                <div className={classes.itemText}>
                  <div className={classes.personName}>
                    {birthdayUser.name}
                    {birthdayUser.id === user?.id && " (Você!)"}
                  </div>
                  <div className={classes.personType}>
                    <PersonIcon />
                    <span>Usuário do Sistema</span>
                  </div>
                </div>

                <div className={classes.itemAction}>
                  <Chip
                    size="small"
                    icon={<CakeIcon />}
                    label={formatAge(birthdayUser.age)}
                  />
                </div>
              </li>
            ))}

            {/* Aniversariantes Contatos */}
            {birthdayData.contacts.map((birthdayContact) => (
              <li key={`contact-${birthdayContact.id}`} className={classes.item}>
                <Avatar className={classes.avatar}>
                  {getInitials(birthdayContact.name)}
                </Avatar>

                <div className={classes.itemText}>
                  <div className={classes.personName}>
                    {birthdayContact.name}
                  </div>
                  <div className={classes.personType}>
                    <PhoneIcon />
                    <span>Contato</span>
                    <Chip
                      icon={<CakeIcon />}
                      label={formatAge(birthdayContact.age)}
                      size="small"
                    />
                  </div>
                </div>

                <div className={classes.itemAction}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={() => handleSendBirthdayMessage(
                      birthdayContact.id, 
                      birthdayContact.name
                    )}
                    disabled={
                      sendingMessages.has(birthdayContact.id) || 
                      birthdayContact.messageSent
                    }
                  >
                    {sendingMessages.has(birthdayContact.id) 
                      ? "Enviando..." 
                      : birthdayContact.messageSent
                      ? "Mensagem já enviada"
                      : "Enviar Parabéns"
                    }
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>

      {/* Actions */}
      <DialogActions>
        <Button onClick={onClose}>
          Lembrar mais tarde
        </Button>
        <Button 
          onClick={onClose} 
          color="primary"
          variant="contained"
        >
          Entendi, obrigado!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BirthdayModal;
